import { brand } from "../theme/brand.config";

// In-memory bearer (never localStorage — survives a tab, not an XSS
// exfiltration window any longer than it must).
let token: string | null = null;

const base = brand.apiBaseUrl;

export type SignupStatus = "pending" | "approved" | "rejected";

export interface Session {
  authenticated: boolean;
  sub?: string;
  // Provisioned = the user has a portal account/membership and the
  // admin API will accept them. False during the private-preview wait
  // (and remains false for declined users); the SPA renders the
  // Pending gate page in that case rather than the dashboard.
  provisioned?: boolean;
  signup_status?: SignupStatus | null;
}

// ``includeProvisioning=true`` (the default for the SessionProvider's
// first call) asks the worker for the signup-queue / account state
// alongside the refreshed access token. The 401-retry path inside
// ``call()`` doesn't need that state — it only needs a fresh token —
// so it passes ``false`` and the worker skips the DB round-trip,
// keeping the refresh path zero-DB.
export async function bootstrapSession(
  includeProvisioning = true,
): Promise<Session> {
  const url = includeProvisioning
    ? `${base}/auth/session?bootstrap=1`
    : `${base}/auth/session`;

  // Resilient fetch: a single transient failure must NOT log the user out.
  // The worker only ever returns 401 for a real "no/invalid session"; a
  // 403 / 5xx / 429 / network error on this path has been observed to come
  // from intermittent Cloudflare edge mitigation (no Security Event, not
  // request-content-driven — the identical request succeeds on retry). So
  // we retry those a few times with backoff and ONLY treat 401 (or a
  // persistent failure) as logged-out. Without this, a momentary edge 403
  // bounces an authenticated user to the login screen.
  let res: Response | null = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      // cache: "no-store" — never let the browser serve /auth/session from
      // bfcache/disk cache. A stale cached 403 (observed: normal reload
      // 403s + logs out, but "empty cache and hard reload" logs straight
      // back in) would otherwise be replayed on every reload AND defeat the
      // retry loop below (all attempts hit the same cached entry). Forces a
      // real network round-trip each call.
      res = await fetch(url, { credentials: "include", cache: "no-store" });
    } catch {
      res = null; // network error → treat as transient, retry
    }
    // 401 = definitively no session: stop, report logged-out (no retry).
    if (res && res.status === 401) return { authenticated: false };
    if (res && res.ok) break;
    // Transient (403 / 5xx / 429 / network): back off and retry.
    if (attempt < 2) await new Promise((r) => setTimeout(r, 300 * (attempt + 1)));
  }
  if (!res || !res.ok) return { authenticated: false };
  const j = (await res.json()) as {
    token: string;
    sub: string;
    provisioned?: boolean;
    signup_status?: SignupStatus | null;
  };
  token = j.token;
  return {
    authenticated: true,
    sub: j.sub,
    provisioned: j.provisioned ?? false,
    signup_status: j.signup_status ?? null,
  };
}

export type AuthIntent = "signup" | "signin";

// ``intent`` differentiates which hosted screen WorkOS lands on first
// (request-access vs returning sign-in). The OAuth callback path and
// the signup-queue gate behave identically either way — the intent
// is a UX hint, not an authorization decision.
export function loginUrl(intent?: AuthIntent): string {
  return intent ? `${base}/auth/login?intent=${intent}` : `${base}/auth/login`;
}

// Local-dev sign-in bypass. The worker only honours this when
// ENVIRONMENT="development"; the button that links here is rendered only
// under Vite dev (import.meta.env.DEV), so it never ships in a build.
export function devLoginUrl(): string {
  return `${base}/auth/dev-login`;
}

export async function logout(): Promise<void> {
  await fetch(`${base}/auth/logout`, {
    method: "POST",
    credentials: "include",
  });
  token = null;
  // Broadcast so any session-scoped module caches (e.g.
  // usePlatformAdmin's "am I an admin?" promise) can invalidate.
  // The current call sites all follow logout() with
  // location.reload(), which also drops the caches; the event
  // exists so a future soft-logout path doesn't leave stale
  // admin-status visibility behind.
  window.dispatchEvent(new Event("kyb-session-cleared"));
}

function doFetch(path: string, init: RequestInit): Promise<Response> {
  return fetch(`${base}${path}`, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init.body ? { "content-type": "application/json" } : {}),
    },
  });
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res = await doFetch(path, init);
  // The in-memory access token is short-lived. On a 401, transparently
  // refresh it via /auth/session (which uses the httpOnly refresh-token
  // cookie) and retry once before surfacing the error. ``false`` skips
  // the provisioning-state DB lookup on the worker side — this is the
  // hot path and only needs a fresh token.
  if (res.status === 401) {
    const s = await bootstrapSession(false);
    if (s.authenticated) res = await doFetch(path, init);
  }
  if (!res.ok) {
    throw new Error(
      ((await res.json().catch(() => ({}))) as { error?: string }).error ??
        `http_${res.status}`,
    );
  }
  return res.json() as Promise<T>;
}

export interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  last4: string;
  scopes: string[];
  status: string;
  last_used_at: string | null;
  created_at: string;
}

// POST /v1/admin/keys returns ONLY these fields (the plaintext secret plus
// identifiers) — NOT the full ApiKey row. In particular it carries no
// `scopes`/`status`/`created_at`, so the create-key caller must not read
// those off the response (see routes/admin.ts). The secret is returned
// exactly once and never persisted.
export interface CreatedApiKey {
  id: string;
  name: string;
  prefix: string;
  last4: string;
  key: string;
}

// Rendered verbatim on both Dashboard.tsx and ApiKeys.tsx — centralised so
// the auth-scheme string (and the `kyb_live_` key prefix it advertises)
// can't drift between the two pages.
export const REST_AUTH_EXAMPLE = "Authorization: Bearer kyb_live_…";

export interface SignupRequest {
  workos_user_id: string;
  email: string;
  status: SignupStatus;
  requested_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  notes: string | null;
}

export interface Watchlist {
  id: string;
  company_id: string;
  company_name: string | null;
  event_types: string[];
  webhook_url: string | null;
  notify_email: string | null;
  email_cadence: "daily" | "prompt_and_daily" | null;
  status: string;
  created_at: string;
}

export interface WatchlistsResponse {
  watchlists: Watchlist[];
  // Capability bits the page renders against: email delivery is on at
  // every coverage tier (false only in the locked/lapsed state);
  // member_emails are the only addresses the worker accepts as
  // notify_email (WorkOS-verified members). watch_limit is the coverage
  // cap so the page can show "Tracking N of {watch_limit} companies".
  email_delivery: boolean;
  watch_limit: number;
  member_emails: string[];
}

export interface CreateWatchlistRequest {
  company_id: string;
  company_name?: string;
  event_types: string[];
  notify_email?: string;
  email_cadence?: "daily" | "prompt_and_daily";
}

// A ranked candidate from the fuzzy company-name search. Mirrors the
// upstream /search hit shape (pipeline/pipeline/api/search.py). `score`
// is a heuristic 0..1 composite, NOT an identity confirmation.
export interface CompanyHit {
  entity_id: string;
  schema: string;
  matched_name: string;
  matched_kind: string;
  score: number;
  jurisdiction: string | null;
}

export interface PlanCatalogEntry {
  provider_variant_id: string;
  plan: string;
  display_name: string | null;
  monthly_price_cents: number | null;
  included_units: number;
  rate_limit_rpm: number;
  watch_limit: number;
  email_delivery: boolean;
  // Coverage repackage: metering mode the plan grants ('coverage' |
  // 'metered'). Drives whether the row reads as a coverage tier or the
  // developer (unit-metered) plan.
  read_model: string;
  // Billing cadence this variant sells (0006 migration): 'month' | 'year'.
  // monthly_price_cents holds the per-interval price — for 'year' rows
  // that's the annual total (the column name is historical). Annual rows
  // share the same `plan` name as their monthly sibling; the SPA groups
  // by `plan` and uses this to pick which variant a Monthly/Annual toggle
  // shows.
  billing_interval: "month" | "year";
}

// Full entitlement shape as GET /v1/admin/billing returns it (workstream
// C3 extraction — was inlined in BillingState). GET /v1/admin/dashboard
// returns a TRIMMED entitlement (see DashboardEntitlement below) carrying
// only the fields the dashboard renders; both are structurally compatible
// so a component typed against the trimmed shape (BillingBanner) accepts
// either.
export interface Entitlement {
  plan: string;
  included_units: number;
  rate_limit_rpm: number;
  watch_limit: number;
  email_delivery: boolean;
  status: string;
  grace_until: string | null;
  billing_provider: string | null;
  // Coverage repackage. read_model: 'coverage' | 'metered' | 'locked'
  // (locked = pre-checkout / lapsed). trial_ends_at is the display-only
  // LS trial end ("N days left"), null when not on trial.
  read_model: string;
  trial_ends_at: string | null;
}

export interface BillingState {
  entitlement: Entitlement | null;
  plans: PlanCatalogEntry[];
  checkout_configured: boolean;
}

// The subset of Entitlement that GET /v1/admin/dashboard's aggregate
// query selects (portal/worker/src/routes/dashboard_data.ts: `SELECT
// e.plan, e.read_model, e.watch_limit, e.email_delivery,
// e.trial_ends_at, e.grace_until`) — deliberately narrower than the
// full Billing entitlement, so keep this Pick in lockstep with that
// SELECT rather than widening it speculatively.
export type DashboardEntitlement = Pick<
  Entitlement,
  | "plan"
  | "read_model"
  | "watch_limit"
  | "email_delivery"
  | "trial_ends_at"
  | "grace_until"
>;

// One row from GET /v1/admin/dashboard's recent_events (mirrors
// RecentWatchEventRow in portal/worker/src/db/repo.ts — no payload/
// watchlist_id, just the fields the activity feed renders).
export interface WatchEventItem {
  id: string;
  event_kind: string;
  company_id: string;
  company_name: string | null;
  occurred_at: string; // ISO-8601 UTC
}

export interface DashboardResponse {
  entitlement: DashboardEntitlement | null;
  watchlists: Watchlist[];
  recent_events: WatchEventItem[];
  events_7d: number;
  // Top-level convenience mirror of entitlement.watch_limit — null only
  // when the account has no entitlement row at all.
  watch_limit: number | null;
}

// A filing-derived headline figure, preserved verbatim from the upstream
// financial-summary envelope (pipeline/pipeline/api/financial_summary.py):
// "reported" = straight from the parsed filing, "derived" = computed
// (e.g. runway), "unavailable" = the concept has no value this period.
// Never relabel these as advice — see CLAUDE.md invariant 3.
export interface MetricEnvelope {
  value: number | null;
  source: "reported" | "derived" | "unavailable";
}

// GET /v1/admin/dashboard/company/:id/summary's success DTO (mirrors the
// `dto` object built in portal/worker/src/routes/dashboard_data.ts).
export interface CompanySummary {
  company_id: string;
  currency: string | null;
  current_period_end: string | null;
  turnover: MetricEnvelope;
  profit_loss: MetricEnvelope;
  cash: MetricEnvelope;
  runway_months: MetricEnvelope;
  hygiene_score: number | null;
  fetched_at: string; // ISO-8601
}

// ─── company detail (deep-metered "view all data" page) ─────────────────
//
// Response shapes for GET /v1/admin/dashboard/company/:id/detail (and its
// /quote sibling). Sections mirror the upstream envelopes verbatim
// (portal/worker/src/routes/dashboard_data.ts DETAIL_SEGMENTS); only the
// fields the page renders are typed — responses may carry more.

// Per-numeric provenance envelope, as upstream emits it. Wider than
// MetricEnvelope: parsed surfaces also stamp "ixbrl" / "pdf_heuristic" / …
// Never relabel these — filing-derived figures stay labelled (CLAUDE.md
// invariant 4).
export interface FieldEnvelope {
  value: number | null;
  source: string;
}

export interface DetailQuote {
  company_id: string;
  // true = locked account, non-showcase company → needs a subscription.
  blocked: boolean;
  unit_cost: 0 | 1 | null;
  reason: "watched" | "showcase" | null;
  read_model: string;
}

// Minimal state the Companies page needs to render per-hit cost badges —
// the account's metering mode and its watched-company id set. Deliberately
// lighter than the full dashboard aggregate (no events feed / counts).
export interface CompanyDetailContext {
  read_model: string;
  watched_ids: string[];
}

export interface CompanyProfileDto {
  company_id: string;
  found: boolean;
  name: string | null;
  status: string | null;
  legal_form: string | null;
  incorporation_date: string | null;
  dissolution_date: string | null;
  registered_office: string | null;
  registration_number: string | null;
  sic_codes: string[];
  previous_names: string[];
  accounts_category: string | null;
  accounts_last_made_up: string | null;
  accounts_next_due: string | null;
  confirmation_statement_last: string | null;
  confirmation_statement_next_due: string | null;
  snapshot_date?: string;
  last_changed?: string | null;
  monitored?: boolean;
  synced_through?: string | null;
}

export interface FinancialSummaryDto {
  current_period_end: string | null;
  prior_period_end: string | null;
  currency: string | null;
  headline: Record<string, FieldEnvelope>;
  growth: Record<string, FieldEnvelope>;
  runway: Record<string, FieldEnvelope>;
  ratios: Record<string, FieldEnvelope>;
  profitability_flag: boolean | null;
  parse_status: string;
  parse_confidence: string | null;
}

export interface AccountsPeriodDto {
  period_end: string | null;
  period_start: string | null;
  currency: string | null;
  balance_sheet: Record<string, FieldEnvelope>;
  profit_and_loss: Record<string, FieldEnvelope>;
  cash_flow?: Record<string, FieldEnvelope>;
  notes?: Record<string, FieldEnvelope>;
}

export interface AccountsHistoryDto {
  coverage?: {
    period_count: number;
    earliest_period: string | null;
    latest_period: string | null;
  };
  periods?: AccountsPeriodDto[];
}

export interface FilingItemDto {
  transaction_id?: string;
  type?: string;
  category?: string | null;
  date?: string | null;
  description?: string | null;
  pages?: number | null;
  paper_filed?: boolean;
}

export interface FilingsDto {
  items: FilingItemDto[];
  total_count?: number;
}

export interface ShareCapitalDto {
  statement_of_capital_date: string | null;
  currency: string | null;
  share_classes: {
    share_class: string | null;
    currency: string | null;
    issued_count: number | null;
    aggregate_nominal: number | null;
    paid_up_per_share: number | null;
  }[];
  total_issued_shares: number | null;
  parse_status: string;
  parse_confidence: string | null;
}

export interface ValuationDto {
  as_of_sh01_date: string | null;
  currency: string | null;
  post_money: number | null;
  pre_money: number | null;
  price_per_share: number | null;
  confidence: string | null;
  confidence_reasons?: string[] | null;
  parse_status: string;
  // "accounts_share_issuance" for the accounts-derived fallback tier (with its
  // period_end), absent/other for the as-reported SH01 basis. Lets the renderer
  // cite the right provenance instead of always claiming an SH01 source.
  valuation_basis?: string | null;
  period_end?: string | null;
}

export interface FundingRoundDto {
  allotment_date: string | null;
  share_class?: string | null;
  currency?: string | null;
  shares_allotted?: number | null;
  price_per_share: number | null;
  pre_money: number | null;
  post_money: number | null;
  confidence: string | null;
  confidence_reasons?: string[] | null;
}

export interface FundingRoundsDto {
  currency?: string | null;
  rounds: FundingRoundDto[];
  parse_status: string;
}

export interface ChargeItemDto {
  charge_id?: string;
  status: string;
  created_on: string | null;
  satisfied_on: string | null;
  classification: string | null;
  persons_entitled: string[];
  secured_description?: string | null;
  is_floating_charge?: boolean | null;
}

export interface ChargesDto {
  total_count: number;
  satisfied_count: number;
  outstanding_count: number;
  items: ChargeItemDto[];
  parse_status?: string;
}

export interface ConsolidationItemDto {
  company_id: string;
  lei: string;
  name: string | null;
  jurisdiction: string | null;
  relationship: string;
  start_date: string | null;
}

export interface ConsolidationDto {
  lei: string | null;
  parents: ConsolidationItemDto[];
  subsidiaries: ConsolidationItemDto[];
  parse_status: string;
  source: string;
  as_of: string | null;
}

export interface GovernanceDto {
  signals: Record<string, FieldEnvelope | unknown>;
  hygiene_score: number | null;
  parse_confidence: string | null;
  parse_status: string;
}

export interface CompanyEventDto {
  event_id: string;
  event_type: string;
  event_date: string | null;
  polarity?: string | null;
  severity?: number | null;
  confidence?: string | null;
  source?: string | null;
}

export interface EventsDto {
  count: number;
  events: CompanyEventDto[];
  parse_status: string;
}

export interface CompanyDetail {
  company_id: string;
  // What THIS view actually drew from the unit budget (0 for watched /
  // showcase companies), and why it was free when it was.
  charged_units: number;
  free_reason: "watched" | "showcase" | null;
  fetched_at: string;
  profile: CompanyProfileDto;
  financial_summary: FinancialSummaryDto | null;
  accounts_history: AccountsHistoryDto | null;
  filings: FilingsDto | null;
  share_capital: ShareCapitalDto | null;
  latest_valuation: ValuationDto | null;
  funding_rounds: FundingRoundsDto | null;
  charges: ChargesDto | null;
  consolidation: ConsolidationDto | null;
  governance_signals: GovernanceDto | null;
  events: EventsDto | null;
}

// ``return_path`` picks which first-party page Lemon Squeezy redirects the
// buyer back to after checkout ("welcome" — new/locked accounts finishing
// setup; "billing" — existing accounts switching plans). It's a fixed enum
// mirrored from the worker's zod schema, never a caller-supplied URL.
export type CheckoutReturnPath = "welcome" | "billing";

export const api = {
  billing: () => call<BillingState>("/v1/admin/billing"),
  createCheckout: (variant_id: string, return_path: CheckoutReturnPath = "billing") =>
    call<{ url: string }>("/v1/admin/billing/checkout", {
      method: "POST",
      body: JSON.stringify({ variant_id, return_path }),
    }),
  me: () =>
    call<{ email: string; is_platform_admin: boolean; role: string }>(
      "/v1/admin/me",
    ),
  dashboard: () => call<DashboardResponse>("/v1/admin/dashboard"),
  companySummary: (companyId: string) =>
    call<CompanySummary>(
      `/v1/admin/dashboard/company/${encodeURIComponent(companyId)}/summary`,
    ),
  // Lightweight context for the Companies search page's cost badges.
  companyDetailContext: () =>
    call<CompanyDetailContext>("/v1/admin/dashboard/company-detail-context"),
  // Free pre-flight: what would viewing this company's full data cost?
  companyDetailQuote: (companyId: string) =>
    call<DetailQuote>(
      `/v1/admin/dashboard/company/${encodeURIComponent(companyId)}/detail/quote`,
    ),
  // Deep-METERED: draws the graded unit cost from the account's included
  // units (0 for watched/showcase companies) — quote first, then confirm.
  companyDetail: (companyId: string) =>
    call<CompanyDetail>(
      `/v1/admin/dashboard/company/${encodeURIComponent(companyId)}/detail`,
    ),
  listWatchlists: () =>
    call<WatchlistsResponse>("/v1/admin/watchlists"),
  searchCompanies: (q: string) =>
    call<{ hits: CompanyHit[] }>(
      `/v1/admin/company-search?q=${encodeURIComponent(q)}`,
    ),
  createWatchlist: (body: CreateWatchlistRequest) =>
    call<{ watchlist: Watchlist; was_new: boolean }>("/v1/admin/watchlists", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  revokeWatchlist: (id: string) =>
    call<{ ok: boolean }>(
      `/v1/admin/watchlists/${encodeURIComponent(id)}/revoke`,
      { method: "POST" },
    ),
  listSignups: (status: SignupStatus | "all" = "pending") =>
    call<{ requests: SignupRequest[] }>(
      `/v1/admin/signups?status=${encodeURIComponent(status)}`,
    ),
  approveSignup: (workosUserId: string, notes?: string) =>
    call<{ request: SignupRequest }>(
      `/v1/admin/signups/${encodeURIComponent(workosUserId)}/approve`,
      {
        method: "POST",
        body: JSON.stringify({ notes: notes ?? "" }),
      },
    ),
  rejectSignup: (workosUserId: string, notes?: string) =>
    call<{ request: SignupRequest }>(
      `/v1/admin/signups/${encodeURIComponent(workosUserId)}/reject`,
      {
        method: "POST",
        body: JSON.stringify({ notes: notes ?? "" }),
      },
    ),
  // Comp / courtesy plan grant (platform admin only). Targets the account
  // by member email — the worker resolves it and the path id is ignored
  // when an email is present, so "-" is a deliberate placeholder. plan is
  // a catalog tier name, or "locked" to revoke back to the no-plan state.
  setAccountPlan: (email: string, plan: string, reason?: string) =>
    call<{ ok: boolean }>("/v1/admin/accounts/-/plan", {
      method: "POST",
      body: JSON.stringify({ email, plan, ...(reason ? { reason } : {}) }),
    }),
  listKeys: () => call<{ keys: ApiKey[] }>("/v1/admin/keys"),
  createKey: (body: { name: string; scopes: string[] }) =>
    call<CreatedApiKey>("/v1/admin/keys", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  revokeKey: (id: string) =>
    call<{ ok: true }>(`/v1/admin/keys/${id}/revoke`, { method: "POST" }),
  usage: () =>
    call<{
      rollup: { day: string; route_class: string; units: number }[];
      entitlement: { plan: string; included_units: number; status: string } | null;
      by_channel: { rest: number; mcp: number };
    }>("/v1/admin/usage"),
  connectedApps: () =>
    call<{ grants: { id: string; client_name: string; scopes: string[]; created_at: string }[] }>(
      "/v1/admin/connected-apps",
    ),
  revokeApp: (id: string) =>
    call<{ ok: true }>(`/v1/admin/connected-apps/${id}/revoke`, {
      method: "POST",
    }),
  relabelApp: (id: string, client_name: string) =>
    call<{ ok: true }>(`/v1/admin/connected-apps/${id}/label`, {
      method: "POST",
      body: JSON.stringify({ client_name }),
    }),
};
